import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Paper,
  IconButton,
  Box,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../globals/utils/api";
import { toast } from "react-toastify";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

function BondNumberList() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openReserveDialog, setOpenReserveDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [bondStart, setBondStart] = useState("");
  const [bondEnd, setBondEnd] = useState("");
  const [selectedBondNumber, setSelectedBondNumber] = useState(null);
  const [selectedBondId, setSelectedBondId] = useState(null);
  const [viewBond, setViewBond] = useState(null);
  const [bondNumbers, setBondNumbers] = useState([]);
  const [reservedBonds, setReservedBonds] = useState([]);
  const [bondTypes, setBondTypes] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [selectedCarrier, setSelectedCarrier] = useState("");
  const [dialogCarrier, setDialogCarrier] = useState(""); // Separate state for dialog carrier selection
  const [usageFilter, setUsageFilter] = useState(""); // "" = All, "used" = Used, "unused" = Unused
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bondType, setBondType] = useState("");
  const [bondSubCategory, setBondSubCategory] = useState("");
  const [bondAmount, setBondAmount] = useState("");
  const [principal, setPrincipal] = useState("");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchBondNumbers(),
        fetchReservedBonds(),
        fetchBondTypes(),
        fetchCarriers(),
      ]);
    } catch (error) {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchBondNumbers = async () => {
    const response = await apiClient("/bondnumbers/", "GET");
    setBondNumbers(response || []);
  };

  const fetchReservedBonds = async () => {
    const response = await apiClient("/reservedbonds/", "GET");
    setReservedBonds(response || []);
  };

  const fetchBondTypes = async () => {
    const response = await apiClient("/bondtypes/", "GET");
    setBondTypes(response || []);
  };

  const fetchCarriers = async () => {
    const response = await apiClient("/carriers/0/", "GET");
    setCarriers(response?.data || []);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setPage(0);
    setUsageFilter(""); // Reset usage filter when switching tabs
  };

  const handleCreateBondRange = async () => {
    if (!dialogCarrier) {
      toast.error("Please select a carrier.");
      return;
    }

    setSubmitting(true);
    try {
      await apiClient("/bondnumbers/generate/", "POST", {
        start_number: bondStart,
        end_number: bondEnd,
        carrier_id: dialogCarrier,
      });
      toast.success("Bond numbers created successfully.");
      await fetchBondNumbers();
    } catch (error) {
      toast.error("Failed to create bond numbers.");
    } finally {
      setSubmitting(false);
      handleCloseDialog();
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setBondStart("");
    setBondEnd("");
    setDialogCarrier("");
  };

  const handleOpenReserveDialog = (bondNumber, bondId) => {
    setSelectedBondNumber(bondNumber);
    setSelectedBondId(bondId);
    setOpenReserveDialog(true);
  };

  const handleCloseReserveDialog = () => {
    setOpenReserveDialog(false);
    setSelectedBondNumber(null);
    setSelectedBondId(null);
  };

  const handleReserveBond = async (bondData) => {
    setSubmitting(true);
    try {
      await apiClient("/reservedbonds/", "POST", {
        ...bondData,
        bond_number: selectedBondNumber,
        bond_number_id: selectedBondId,
      });
      toast.success("Bond reserved successfully.");
      await Promise.all([fetchBondNumbers(), fetchReservedBonds()]);
    } catch (error) {
      toast.error("Failed to reserve bond.");
    } finally {
      setSubmitting(false);
      handleCloseReserveDialog();
    }
  };

  const toggleBondUsage = async (bondno_id) => {
    setSubmitting(true);
    try {
      await apiClient(`/bondnumbers/${bondno_id}/toggle/`, "PATCH");
      await Promise.all([fetchBondNumbers(), fetchReservedBonds()]);
      toast.success("Bond usage status updated.");
    } catch (error) {
      toast.error("Failed to update bond usage.");
    } finally {
      setSubmitting(false);
    }
  };

  const getCarrierNameById = (id) => {
    console.log(carriers.find((c) => c.bond_carrier_id === id)?.name);
    return carriers.find((c) => c.bond_carrier_id === id)?.name;
  };

  const filteredBonds = (tab === 0 ? bondNumbers : reservedBonds).filter(
    (bond) =>
      bond.bond_number?.toString().includes(searchQuery) &&
      (selectedCarrier
        ? getCarrierNameById(bond.carrier) ===
          getCarrierNameById(selectedCarrier)
        : true) &&
      (usageFilter === ""
        ? true
        : usageFilter === "used"
        ? bond.is_used === true
        : bond.is_used === false)
  );

  const currentRows = filteredBonds.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Navbar />
      <Box sx={{ maxWidth: "100%", padding: "20px" }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
          Bond Number Management
        </Typography>

        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{ marginBottom: "20px" }}
        >
          <Tab label="All Bond Numbers" />
          <Tab label="Reserved Bonds" />
        </Tabs>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            marginBottom: "20px",
            alignItems: "center",
          }}
        >
          <TextField
            label="Search by Bond Number"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Filter by Carrier"
            value={selectedCarrier}
            onChange={(e) => {
              setSelectedCarrier(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 250 }}
          >
            <MenuItem value="">All Carriers</MenuItem>
            {carriers.map((carrier) => (
              <MenuItem
                key={carrier.bond_carrier_id}
                value={carrier.bond_carrier_id}
              >
                {carrier.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Filter by Usage"
            value={usageFilter}
            onChange={(e) => {
              setUsageFilter(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All Bond Numbers</MenuItem>
            <MenuItem value="used">Used</MenuItem>
            <MenuItem value="unused">Unused</MenuItem>
          </TextField>
          {tab === 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              New Bond Numbers
            </Button>
          )}
        </Box>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ height: "300px" }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ width: "100%", overflowX: "auto", mb: 2 }}
          >
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell>Bond Number</TableCell>
                  <TableCell>Carrier</TableCell>
                  <TableCell align="center">Is Used</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Used By</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((bond) => (
                  <TableRow key={bond.bondno_id || bond.reserved_id}>
                    <TableCell>{bond.bond_number}</TableCell>
                    <TableCell>
                      {getCarrierNameById(bond?.carrier) || "-"}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={bond.is_used}
                        onChange={() => toggleBondUsage(bond.bondno_id)}
                        color="primary"
                        disabled={submitting}
                      />
                    </TableCell>
                    <TableCell>
                      {bond.created_at
                        ? new Date(bond.created_at).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell>{bond.used_by || "Not used yet"}</TableCell>
                    <TableCell align="center">
                      {tab === 0 ? (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() =>
                            handleOpenReserveDialog(
                              bond.bond_number,
                              bond.bondno_id
                            )
                          }
                          disabled={submitting}
                        >
                          Reserve
                        </Button>
                      ) : (
                        <IconButton
                          color="primary"
                          onClick={() => {
                            setViewBond(bond);
                            setOpenViewDialog(true);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={filteredBonds.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Box>

      {/* Create Bond Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Create New Bond Numbers</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Select Carrier"
            value={dialogCarrier}
            onChange={(e) => setDialogCarrier(e.target.value)}
            sx={{ mt: 1 }}
          >
            {carriers.map((carrier) => (
              <MenuItem
                key={carrier.bond_carrier_id}
                value={carrier.bond_carrier_id}
              >
                {carrier.name}
              </MenuItem>
            ))}
          </TextField>

          {getCarrierNameById(dialogCarrier) === "Liberty Mutual Insurance Company" ? (
            <>
              <TextField
                margin="dense"
                label="Bond Start Number"
                fullWidth
                variant="outlined"
                value={bondStart}
                onChange={(e) => setBondStart(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Bond End Number"
                fullWidth
                variant="outlined"
                value={bondEnd}
                onChange={(e) => setBondEnd(e.target.value)}
              />
            </>
          ) : (
            dialogCarrier && (
              <Typography sx={{ mt: 2, color: "red" }}>
                This carrier is not yet supported for bond number generation.
              </Typography>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleCreateBondRange}
            color="primary"
            disabled={
              submitting ||
              getCarrierNameById(dialogCarrier) !== "Liberty Mutual Insurance Company"
            }
          >
            {submitting ? <CircularProgress size={24} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Reserved Bond Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)}>
        <DialogTitle>Reserved Bond Details</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Bond Number"
            fullWidth
            variant="outlined"
            value={viewBond?.bond_number || ""}
            disabled
          />
          <TextField
            margin="dense"
            label="Bond Type"
            fullWidth
            variant="outlined"
            value={viewBond?.bond_type || ""}
            disabled
          />
          <TextField
            margin="dense"
            label="Bond Sub Category"
            fullWidth
            variant="outlined"
            value={viewBond?.bond_sub_category || ""}
            disabled
          />
          <TextField
            margin="dense"
            label="Bond Amount"
            fullWidth
            variant="outlined"
            value={viewBond?.bond_amount || ""}
            disabled
          />
          <TextField
            margin="dense"
            label="Principal / Deceased Legal Name"
            fullWidth
            variant="outlined"
            value={viewBond?.deceased_legal_name || ""}
            disabled
          />
          <TextField
            margin="dense"
            label="Is Used"
            fullWidth
            variant="outlined"
            value={viewBond?.is_used ? "Yes" : "No"}
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reserve Dialog */}
      <Dialog open={openReserveDialog} onClose={handleCloseReserveDialog}>
        <DialogTitle>Reserve Bond Number {selectedBondNumber}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Bond Type"
            select
            fullWidth
            variant="outlined"
            required
            value={bondType}
            onChange={(e) => setBondType(e.target.value)}
          >
            {bondTypes.map((type) => (
              <MenuItem key={type.bond_type_id} value={type.type}>
                {type.type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Bond Sub Category"
            fullWidth
            variant="outlined"
            value={bondSubCategory}
            onChange={(e) => setBondSubCategory(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Bond Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={bondAmount}
            onChange={(e) => setBondAmount(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Principal"
            fullWidth
            variant="outlined"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReserveDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() =>
              handleReserveBond({
                bond_type: bondType,
                bond_sub_category: bondSubCategory,
                bond_amount: bondAmount,
                deceased_legal_name: principal,
              })
            }
            color="primary"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : "Reserve"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BondNumberList;
